import { useEffect, useState } from 'react';
import { Box, Button, Layer } from 'grommet';
import { observer } from 'mobx-react-lite';
import { Card, CardBody, CardHeader, MaskedInput, Select } from '/src/components';
import { useGlobalStore } from '/src/context';
import { ReferralPartner } from '/src/lib/models';

type AddReferralCommissionModalProps = {
  onClose: () => void;
  onSubmit: (referralPartnerId: number, percentRate: string) => Promise<void>;
  isLoading: boolean;
};

export const AddReferralCommissionModal: React.FC<AddReferralCommissionModalProps> = observer(
  ({ onClose, onSubmit, isLoading }) => {
    const globalStore = useGlobalStore();
    const [referralPartnerId, setReferralPartnerId] = useState<number | null>(null);
    const [percentRate, setPercentRate] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [referralPartners, setReferralPartners] = useState<ReferralPartner[]>([]);
    const [isLoadingPartners, setIsLoadingPartners] = useState(true);

    useEffect(() => {
      const loadReferralPartners = async () => {
        try {
          setIsLoadingPartners(true);
          await globalStore.fetchReferralPartners();
          setReferralPartners(globalStore.referralPartners || []);
        } catch (error) {
          console.error('Failed to load referral partners:', error);
        } finally {
          setIsLoadingPartners(false);
        }
      };

      loadReferralPartners();
    }, [globalStore]);

    const handleSubmit = async (e: React.FormEvent) => {
      e.preventDefault();

      if (referralPartnerId === null) return;

      try {
        setIsSubmitting(true);
        await onSubmit(referralPartnerId, percentRate);
        onClose();
      } catch (error) {
        console.error('Error in onSubmit:', error);
      } finally {
        setIsSubmitting(false);
      }
    };

    const partnerOptions = referralPartners.map((partner) => ({
      label: partner.name,
      value: partner.id,
    }));

    return (
      <Layer background="transparent">
        <Card width="100%" style={{ maxWidth: 480, minWidth: 320 }}>
          <CardHeader title="Add Referral Commission" />
          <CardBody pad={{ vertical: 'medium', horizontal: 'large' }}>
            <form onSubmit={handleSubmit}>
              <Box gap="medium">
                <Select
                  id="referral_partner_id"
                  name="referral_partner_id"
                  label="Referral Partner"
                  value={referralPartnerId}
                  onChange={({ value }) => setReferralPartnerId(Number(value))}
                  options={partnerOptions}
                  placeholder="Select a referral partner"
                  required
                  disabled={isLoading || isLoadingPartners}
                />

                <MaskedInput
                  label="Commission Rate (%)"
                  value={percentRate}
                  setValue={setPercentRate}
                  componentType="percent"
                  placeholder="0.00"
                  required
                  disabled={isLoading || isSubmitting}
                />

                <Box direction="row" justify="end" gap="small">
                  <Button
                    label="Cancel"
                    onClick={onClose}
                    disabled={isLoading || isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                  <Button
                    type="submit"
                    primary
                    label={isSubmitting ? 'Adding...' : 'Add Commission'}
                    disabled={referralPartnerId === null || !percentRate || isLoading || isSubmitting}
                    style={{ fontWeight: 'normal' }}
                  />
                </Box>
              </Box>
            </form>
          </CardBody>
        </Card>
      </Layer>
    );
  }
);
